import { Module } from 'vuex';
import {
  FilterInfo,
  ShareSurveysKind,
  SpecifiedMethod,
  SurveyExecutionFrom,
  SurveyPromptTemplateTrackingInfo,
  SurveyPromptType,
  SurveyType,
} from '@/types';

const DEFAULT_SESSION_VALUES = {
  question: '',
  filterInfo: {
    searchScope: 'all',
    docTypes: [],
    specifiedFiles: [],
  } as FilterInfo,
  deepMode: false,
  specifiedMethod: undefined,
  executionFrom: undefined,
  recommendedActionId: undefined,
  surveyType: 'survey' as const,
  shareSurveysKind: undefined,
  shareSurveyLastRequestId: undefined,
  shareSurveyPromptTypeName: undefined,
  surveyPromptTemplate: undefined,
};

export type SessionSurveyState = {
  question: string;
  filterInfo: FilterInfo;
  deepMode: boolean;
  lastAccessed: number; // 最終アクセス時刻（ミリ秒）
  // これ以下は計測用の値
  specifiedMethod?: SpecifiedMethod;
  executionFrom?: SurveyExecutionFrom;
  recommendedActionId?: number;
  surveyType: SurveyType;
  shareSurveysKind?: ShareSurveysKind;
  shareSurveyLastRequestId?: string;
  shareSurveyPromptTypeName?: SurveyPromptType['name'];
  surveyPromptTemplate?: SurveyPromptTemplateTrackingInfo;
};

// Mapのkeyはsurveysテーブルのuuid
export type SurveyState = {
  // 各スレッドの画面遷移時の値の受け渡しで使用する状態
  sessionStates: Map<string, SessionSurveyState>;
};

const createInitialSessionState = (): SessionSurveyState => ({
  ...DEFAULT_SESSION_VALUES,
  filterInfo: { ...DEFAULT_SESSION_VALUES.filterInfo },
  lastAccessed: Date.now(),
});

export const initialSurveyState: SurveyState = {
  sessionStates: new Map<string, SessionSurveyState>(),
};

const MAX_SESSION_COUNT = 10;
const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30分

const survey: Module<SurveyState, unknown> = {
  namespaced: true,
  state: () => initialSurveyState,
  mutations: {
    setSessionQuestion(
      state,
      { sessionId, question }: { sessionId: string; question: string },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.question = question;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSessionFilterInfo(
      state,
      { sessionId, filterInfo }: { sessionId: string; filterInfo: FilterInfo },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.filterInfo = filterInfo;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSessionDeepMode(
      state,
      { sessionId, deepMode }: { sessionId: string; deepMode: boolean },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.deepMode = deepMode;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSessionSpecifiedMethod(
      state,
      {
        sessionId,
        specifiedMethod,
      }: { sessionId: string; specifiedMethod?: SpecifiedMethod },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.specifiedMethod = specifiedMethod;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSessionExecutionFrom(
      state,
      {
        sessionId,
        executionFrom,
      }: { sessionId: string; executionFrom: SurveyExecutionFrom },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.executionFrom = executionFrom;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSessionRecommendedActionId(
      state,
      {
        sessionId,
        recommendedActionId,
      }: { sessionId: string; recommendedActionId?: number },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.recommendedActionId = recommendedActionId;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSessionSurveyType(
      state,
      { sessionId, surveyType }: { sessionId: string; surveyType: SurveyType },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.surveyType = surveyType;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSessionShareSurveysKind(
      state,
      {
        sessionId,
        shareSurveysKind,
      }: { sessionId: string; shareSurveysKind?: ShareSurveysKind },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.shareSurveysKind = shareSurveysKind;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSessionShareSurveyLastRequestId(
      state,
      {
        sessionId,
        shareSurveyLastRequestId,
      }: { sessionId: string; shareSurveyLastRequestId?: string },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.shareSurveyLastRequestId = shareSurveyLastRequestId;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSessionShareSurveyPromptTypeName(
      state,
      {
        sessionId,
        shareSurveyPromptTypeName,
      }: {
        sessionId: string;
        shareSurveyPromptTypeName?: SurveyPromptType['name'];
      },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.shareSurveyPromptTypeName = shareSurveyPromptTypeName;
        sessionState.lastAccessed = Date.now();
      }
    },
    setSurveyPromptTemplate(
      state,
      {
        sessionId,
        surveyPromptTemplate,
      }: {
        sessionId: string;
        surveyPromptTemplate?: SurveyPromptTemplateTrackingInfo;
      },
    ) {
      const sessionState = state.sessionStates.get(sessionId);
      if (sessionState) {
        sessionState.surveyPromptTemplate = surveyPromptTemplate;
        sessionState.lastAccessed = Date.now();
      }
    },
    initSessionState(state, sessionId: string) {
      if (state.sessionStates.has(sessionId)) {
        const sessionState = state.sessionStates.get(sessionId)!;
        sessionState.lastAccessed = Date.now();
      } else {
        state.sessionStates.set(sessionId, createInitialSessionState());
      }
    },
    removeSessionState(state, sessionId: string) {
      state.sessionStates.delete(sessionId);
    },
    setSessionAllStateOptimized(
      state,
      {
        sessionId,
        question,
        filterInfo,
        deepMode,
        specifiedMethod,
        executionFrom,
        recommendedActionId,
        surveyType,
        shareSurveysKind,
        shareSurveyLastRequestId,
        shareSurveyPromptTypeName,
        surveyPromptTemplate,
      }: {
        sessionId: string;
        question: string;
        filterInfo: FilterInfo;
        deepMode: boolean;
        specifiedMethod?: SpecifiedMethod;
        executionFrom?: SurveyExecutionFrom;
        recommendedActionId?: number;
        surveyType: SurveyType;
        shareSurveysKind?: ShareSurveysKind;
        shareSurveyLastRequestId?: string;
        shareSurveyPromptTypeName?: SurveyPromptType['name'];
        surveyPromptTemplate?: SurveyPromptTemplateTrackingInfo;
      },
    ) {
      if (!state.sessionStates.has(sessionId)) {
        state.sessionStates.set(sessionId, createInitialSessionState());
      }

      const sessionState = state.sessionStates.get(sessionId)!;
      const now = Date.now();

      sessionState.question = question;
      sessionState.filterInfo = filterInfo;
      sessionState.deepMode = deepMode;
      sessionState.specifiedMethod = specifiedMethod;
      sessionState.executionFrom = executionFrom;
      sessionState.recommendedActionId = recommendedActionId;
      sessionState.surveyType = surveyType;
      sessionState.shareSurveysKind = shareSurveysKind;
      sessionState.shareSurveyLastRequestId = shareSurveyLastRequestId;
      sessionState.shareSurveyPromptTypeName = shareSurveyPromptTypeName;
      sessionState.surveyPromptTemplate = surveyPromptTemplate;
      sessionState.lastAccessed = now;
    },
    cleanupOldSessions(state) {
      const now = Date.now();
      const sessionsToRemove: string[] = [];

      state.sessionStates.forEach((sessionState, sessionId) => {
        if (now - sessionState.lastAccessed > SESSION_TIMEOUT_MS) {
          sessionsToRemove.push(sessionId);
        }
      });

      // 古いセッションを削除
      sessionsToRemove.forEach(sessionId => {
        state.sessionStates.delete(sessionId);
      });

      // セッション数上限チェック
      if (state.sessionStates.size > MAX_SESSION_COUNT) {
        // lastAccessedの降順（新しい順）で並び替え
        const sortedSessionIds = Array.from(state.sessionStates.entries())
          .sort(([, a], [, b]) => b.lastAccessed - a.lastAccessed)
          .map(([sessionId]) => sessionId);

        // 残すべき最新MAX_SESSION_COUNT件を除いた古いsessionIdを削除
        const sessionsToRemove = sortedSessionIds.slice(MAX_SESSION_COUNT);
        for (const sessionId of sessionsToRemove) {
          state.sessionStates.delete(sessionId);
        }
      }
    },
  },
  getters: {
    getSessionQuestion: (state: SurveyState) => (sessionId: string) => {
      const sessionState = state.sessionStates.get(sessionId);
      return sessionState?.question ?? DEFAULT_SESSION_VALUES.question;
    },
    getSessionFilterInfo: (state: SurveyState) => (sessionId: string) => {
      const sessionState = state.sessionStates.get(sessionId);
      return (
        sessionState?.filterInfo ?? { ...DEFAULT_SESSION_VALUES.filterInfo }
      );
    },
    getSessionDeepMode: (state: SurveyState) => (sessionId: string) => {
      const sessionState = state.sessionStates.get(sessionId);
      return sessionState?.deepMode ?? DEFAULT_SESSION_VALUES.deepMode;
    },
    getSessionSpecifiedMethod: (state: SurveyState) => (sessionId: string) => {
      const sessionState = state.sessionStates.get(sessionId);
      return (
        sessionState?.specifiedMethod ?? DEFAULT_SESSION_VALUES.specifiedMethod
      );
    },
    getSessionExecutionFrom: (state: SurveyState) => (sessionId: string) => {
      const sessionState = state.sessionStates.get(sessionId);
      return (
        sessionState?.executionFrom ?? DEFAULT_SESSION_VALUES.executionFrom
      );
    },
    getSessionRecommendedActionId:
      (state: SurveyState) => (sessionId: string) => {
        const sessionState = state.sessionStates.get(sessionId);
        return (
          sessionState?.recommendedActionId ??
          DEFAULT_SESSION_VALUES.recommendedActionId
        );
      },
    getSessionSurveyType: (state: SurveyState) => (sessionId: string) => {
      const sessionState = state.sessionStates.get(sessionId);
      return sessionState?.surveyType ?? DEFAULT_SESSION_VALUES.surveyType;
    },
    getSessionShareSurveysKind: (state: SurveyState) => (sessionId: string) => {
      const sessionState = state.sessionStates.get(sessionId);
      return (
        sessionState?.shareSurveysKind ??
        DEFAULT_SESSION_VALUES.shareSurveysKind
      );
    },
    getSessionShareSurveyLastRequestId:
      (state: SurveyState) => (sessionId: string) => {
        const sessionState = state.sessionStates.get(sessionId);
        return (
          sessionState?.shareSurveyLastRequestId ??
          DEFAULT_SESSION_VALUES.shareSurveyLastRequestId
        );
      },
    getSessionShareSurveyPromptTypeName:
      (state: SurveyState) => (sessionId: string) => {
        const sessionState = state.sessionStates.get(sessionId);
        return (
          sessionState?.shareSurveyPromptTypeName ??
          DEFAULT_SESSION_VALUES.shareSurveyPromptTypeName
        );
      },
    getSurveyPromptTemplate: (state: SurveyState) => (sessionId: string) => {
      const sessionState = state.sessionStates.get(sessionId);
      return (
        sessionState?.surveyPromptTemplate ??
        DEFAULT_SESSION_VALUES.surveyPromptTemplate
      );
    },
    hasSession: (state: SurveyState) => (sessionId: string) =>
      state.sessionStates.has(sessionId),

    // デバッグ用getters
    getAllSessions: (state: SurveyState) =>
      Array.from(state.sessionStates.keys()),
    getSessionCount: (state: SurveyState) => state.sessionStates.size,
  },
  actions: {
    initSession({ commit }, sessionId: string) {
      commit('initSessionState', sessionId);
      commit('cleanupOldSessions');
    },
    removeSession({ commit }, sessionId: string) {
      commit('removeSessionState', sessionId);
    },
    cleanupSessions({ commit }) {
      commit('cleanupOldSessions');
    },
    setSessionAllState(
      { commit },
      {
        sessionId,
        question,
        filterInfo,
        deepMode,
        specifiedMethod,
        executionFrom,
        recommendedActionId,
        surveyType,
        shareSurveysKind,
        shareSurveyLastRequestId,
        shareSurveyPromptTypeName,
        surveyPromptTemplate,
      }: {
        sessionId: string;
        question: string;
        filterInfo: FilterInfo;
        deepMode: boolean;
        specifiedMethod?: SpecifiedMethod;
        executionFrom?: SurveyExecutionFrom;
        recommendedActionId?: number;
        surveyType: SurveyType;
        shareSurveysKind?: ShareSurveysKind;
        shareSurveyLastRequestId?: string;
        shareSurveyPromptTypeName?: SurveyPromptType['name'];
        surveyPromptTemplate?: SurveyPromptTemplateTrackingInfo;
      },
    ) {
      commit('setSessionAllStateOptimized', {
        sessionId,
        question,
        filterInfo,
        deepMode,
        specifiedMethod,
        executionFrom,
        recommendedActionId,
        surveyType,
        shareSurveysKind,
        shareSurveyLastRequestId,
        shareSurveyPromptTypeName,
        surveyPromptTemplate,
      });
    },
  },
};

export default survey;
